import { DeleteOutlined, TranslationOutlined } from '@ant-design/icons';
import MDEditor, { commands } from '@uiw/react-md-editor';
import { Input, Button } from 'antd';
import { useField, FieldArray } from 'formik';
import { memo, useCallback } from 'react';
import styled from 'styled-components';
import FormItem, { type FormItemProps } from '../FormItem';
import { type MarkdownFieldProps, StyledMarkdownWrapper } from '../MarkdownField';
import '@uiw/react-md-editor/markdown-editor.css';

export interface MarkdownFieldArrayProps extends MarkdownFieldProps {
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    isDefault?: boolean;
    disabled?: boolean;
    openTranslationDrawer?: (id: number) => void;
    onDelete?: () => void; // optional because we don't need ondelete for default value
}

export const StyledDiv = styled(Input.Group)`
    display: flex;

    button {
        margin: auto 10px;
        height: 100%;
    }
`;

export const StyledButtonContainer = styled.div`
    background: #fafafa;
    border-top: 1px solid #d9d9d9;
    border-bottom: 1px solid #d9d9d9;
    border-right: 1px solid #d9d9d9;
`;

const MarkdownFieldArray = ({
    name,
    required,
    label,
    isDefault = false,
    openTranslationDrawer,
    onDelete,
    itemProps,
    ...props
}: MarkdownFieldArrayProps) => {
    const [field, meta, helpers] = useField({ name });
    const { value } = meta;
    const onChange = useCallback(
        (e, index) => {
            const newValue = value.map((data, i) => {
                if (index === i) {
                    return {
                        ...data,
                        defaultValue: e.target.value,
                    };
                }

                return data;
            });

            helpers.setValue(newValue);
        },
        [helpers, value]
    );

    return (
        <FormItem {...itemProps} label={label} meta={meta} required={required}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => (
                    <>
                        {value.map((requirement, index) => (
                            <StyledDiv>
                                <StyledMarkdownWrapper data-color-mode="light">
                                    <MDEditor
                                        commands={[
                                            commands.bold,
                                            commands.italic,
                                            commands.strikethrough,
                                            commands.hr,
                                            commands.group([commands.title1, commands.title2], {
                                                name: 'title',
                                                groupName: 'title',
                                                buttonProps: { 'aria-label': 'Insert title' },
                                            }),
                                            commands.divider,
                                            commands.link,
                                            commands.image,
                                            commands.divider,
                                            commands.unorderedListCommand,
                                            commands.orderedListCommand,
                                        ]}
                                        onChange={e => onChange(e, index)}
                                        value={requirement.defaultValue}
                                    />
                                </StyledMarkdownWrapper>
                                {props.disabled ? null : (
                                    <StyledButtonContainer>
                                        <Button
                                            icon={<TranslationOutlined />}
                                            onClick={e => {
                                                openTranslationDrawer(index);
                                            }}
                                            size="small"
                                            style={{ height: '100%' }}
                                            type="text"
                                        />
                                    </StyledButtonContainer>
                                )}
                                {/* we won't show delete button on first entry of default value */}
                                {(!isDefault || (isDefault && index !== 0)) && (
                                    <StyledButtonContainer>
                                        <Button
                                            icon={<DeleteOutlined />}
                                            onClick={() => {
                                                if (value.length === 1) {
                                                    onDelete();
                                                } else {
                                                    arrayHelpers.remove(index);
                                                }
                                            }}
                                            size="small"
                                            style={{ height: '100%' }}
                                            type="text"
                                        />
                                    </StyledButtonContainer>
                                )}
                            </StyledDiv>
                        ))}
                    </>
                )}
            />
        </FormItem>
    );
};

export default memo(MarkdownFieldArray);
